import { CrudPagedListComponent } from '../_directives';
import { Component, ElementRef, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { AlertService, FunnelService, LoaderService, PusherService } from '../_services';
import * as moment from 'moment';
import { NgxSmartModalService } from 'ngx-smart-modal';
import {
  Path,
  PathDateTypes,
  PathPopup,
  PathTableTypes,
  PathTableStatusLabels,
  PathTableStatuses,
  OfferStages,
  GroupedTableData,
  PathSteps,
} from '../_models/funnel';
import { PathService } from '../_services/path.service';
import { Audience, AudienceType, NotificationType, OfferIntents, OfferTypes, Pager, OfferTypeLabels } from '../_models';
import { AudienceService } from '../_services/audience.service';
import { config } from '../../config/config';
import { scrollToAccordionGroup, steps } from '../steps-library/step-util';
import { Filter } from '../_shared';

@Component({
  moduleId: module.id.toString(),
  templateUrl: './path.component.html',
  styleUrls: ['./path.component.scss'],
  selector: 'app-path-list',
})
export class PathComponent extends CrudPagedListComponent implements OnInit {
  pathsList: Path[] = [];
  pageTitle: string = 'Path Builder';
  description: string =
    'Assemble targeted resolution paths for each customer intent using Surveys, Offers, Filters, and Troubleshooters.';
  offerIntentOptions: { value: string; label: string }[] = [];
  showModal: boolean = false;
  pathStatus = PathTableStatuses;
  pathStatusLabels = PathTableStatuses;
  pathDateTypes = PathDateTypes;
  pathTableTypes = PathTableTypes;
  offerIntents = OfferIntents;
  offerIntentLabels = OfferStages;
  showThreeDotMenu: boolean = false;
  selectedRow: number = -1;
  selectedStatus: string = PathTableStatusLabels[PathTableStatuses.All];
  selectedFilter: string = 'name';

  selectedDate: string = PathDateTypes.LastModified;
  searchQuery: string;
  showAddButton: boolean = true;
  showViewButton: boolean = true;
  selectedIndex: number | null = null;
  tableData: any[] = [];
  pathSteps = PathSteps;
  popupData = {
    name: '',
    path_id: null,
    popupType: null,
    offer_intent: OfferIntents.CancelOrder,
    path_steps: [],
    path_audience: null,
    offer_type_options: [],
    id: null,
  };
  originalTableData: any[] = [...this.tableData];
  isLoading = false;
  pageSize = 20;
  PathPopup = PathPopup;
  popupTitle: string = '';
  filters: Filter[] = [
    {
      name: 'Status',
      values: [
        { key: PathTableStatuses.Active, value: PathTableStatusLabels[PathTableStatuses.Active] },
        { key: PathTableStatuses.Inactive, value: PathTableStatusLabels[PathTableStatuses.Inactive] },
        { key: PathTableStatuses.Draft, value: PathTableStatusLabels[PathTableStatuses.Draft] },
      ],
      selectedValues: [],
      showSubPopup: false,
    },
    {
      name: 'Type',
      values: [
        { key: OfferIntents.CancelOrder, value: OfferStages[OfferIntents.CancelOrder] },
        { key: OfferIntents.CancelSubscriptionHold, value: OfferStages[OfferIntents.CancelSubscriptionHold] },
        { key: OfferIntents.CancelRebillHold, value: OfferStages[OfferIntents.CancelRebillHold] },
        { key: OfferIntents.CancelTrialHold, value: OfferStages[OfferIntents.CancelTrialHold] },
        { key: OfferIntents.CancelSubscription, value: OfferStages[OfferIntents.CancelSubscription] },
        { key: OfferIntents.CancelTrial, value: OfferStages[OfferIntents.CancelTrial] },
        { key: OfferIntents.ReturnOrder, value: OfferStages[OfferIntents.ReturnOrder] },
        { key: OfferIntents.ReturnSubscription, value: OfferStages[OfferIntents.ReturnSubscription] },
        { key: OfferIntents.ReturnTrial, value: OfferStages[OfferIntents.ReturnTrial] },
      ],
      selectedValues: [],
      showSubPopup: false,
    },
  ];
  groupedTableData: GroupedTableData[] = [];
  activeAccordion: string | null = null;

  @ViewChildren('accordionGroup') accordionGroups: QueryList<ElementRef>;

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected pathService: PathService,
    protected alertService: AlertService,
    protected funnelService: FunnelService,
    protected loader: LoaderService,
    protected ngxSmartModalService: NgxSmartModalService,
    protected audienceService: AudienceService,
    protected pusherService: PusherService
  ) {
    super(router, location, route, pathService, alertService);
    this.objectName = 'Path';
  }
  ngOnInit() {
    super.ngOnInit();
    this.loader.show();
    this.queryData();
    this.offerIntentOptions = this.getOfferIntentOptions();

    if (this.pusherService.channel) {
      this.pusherService.channel.bind(NotificationType[NotificationType.PATH_PUBLISHING], (data: any) => {
        data = data.payload;
        if (data && data.is_completed) {
          this.queryData();
        }
      });
    }
  }

  queryData() {
    this.loader.show();
    this.pathService.list({ page: 1, page_size: config.maxPageSize }).subscribe(
      (response) => {
        this.loader.hide();
        this.pathsList = response.results;
        const mappedData = response.results.map((item: Path) => {
          const lastModifiedMoment = moment(item.last_modified).tz('EST');

          const lastModified = {
            time: lastModifiedMoment.format('hh:mm A'),
            date: lastModifiedMoment.format('MM/DD/YYYY'),
          };

          return {
            id: item.id,
            displayId: item.display_id,
            pathName: item.name,
            offerIntent: item.offer_intent,
            allSteps: item.steps,
            type: item.offer_intent || '--',
            typeLabel: OfferStages[item.offer_intent],
            status: !item.is_modified ? PathTableStatuses.Active : PathTableStatuses.Draft,
            audience: item.audience || null,
            steps: [
              ...(item.audience
                ? [
                    {
                      id: item.audience.id,
                      name: item.audience.name,
                      category: PathSteps.Audience,
                    },
                  ]
                : []),
              ...item.steps,
            ],
            lastModified,
            showAllConditions: item.steps.length ? false : true,
          };
        });

        this.tableData = mappedData;
        this.originalTableData = mappedData;
        this.groupedTableData = this.groupDataByType(mappedData);
      },
      (error) => {
        this.loader.hide();
        this.alertService.error(error.message);
      }
    );
  }

  createPath() {
    this.popupTitle = 'Create Path';

    this.popupData = {
      ...this.popupData,
      name: '',
      path_id: null,
      popupType: PathPopup.Create,
      offer_intent: null,
      path_steps: [],
      offer_type_options: this.offerIntentOptions,
    };
    this.openPopup();
  }

  editPath(id: number) {
    this.router.navigate(['new-paths', 'edit', id]);
  }

  navigateToAudience(audienceId: number) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['audiences', 'edit', audienceId]));
    window.open(url, '_blank');
  }

  isStepTypeValid(type: number): boolean {
    return (
      !!steps.find((s) => s.type === type) ||
      [OfferTypes.Downsell, OfferTypes.Upsell, OfferTypes.Support].includes(type)
    );
  }

  getOfferIntentOptions() {
    return Object.keys(OfferStages)
      .filter((intentType) => parseInt(intentType, 10) !== OfferIntents.Legacy)
      .map((intentType) => ({
        value: intentType,
        label: OfferStages[intentType],
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  navigateToStep(type: number | string, id: number): void {
    let route: any[] | null = null;

    if (type === PathSteps.Audience) {
      route = ['audiences', 'edit', id];
    } else {
      const stepType = Number(type);
      const step = steps.find((s) => s.type === stepType);

      if (step && step.offer_type === OfferTypes.Survey) {
        route = ['steps', 'survey', 'category', step.offer_type, stepType, 'step', id];
      } else if ([OfferTypes.Downsell, OfferTypes.Upsell, OfferTypes.Support].includes(stepType)) {
        route = ['steps', OfferTypeLabels[stepType], stepType, 'details', id];
      }
    }

    if (route) {
      const url = this.router.serializeUrl(this.router.createUrlTree(route));
      window.open(url, '_blank');
    }
  }

  openPathPopup(
    popupType: number,
    pathId?: number | string,
    pathName?: string,
    offerIntent?: number,
    pathSteps?: { id: number; name: string }[] | null,
    pathAudience?: string | null
  ): void {
    switch (popupType) {
      case PathPopup.Update:
        this.popupTitle = 'Rename Path';
        break;
      case PathPopup.Duplicate:
        this.popupTitle = 'Duplicate Path';
        break;
      case PathPopup.Delete:
        this.popupTitle = 'Delete Path';
        break;
    }

    if (pathAudience) {
      this.audienceService.list({ page: 1, page_size: config.maxPageSize, type: AudienceType.Retention }).subscribe(
        (result: Pager) => {
          const audienceObject = result.results.find((audience: Audience) => audience.name === pathAudience);
          this.popupData.path_audience = audienceObject.id || null;
        },
        (error) => {
          this.handleError(error);
        }
      );
    } else {
      this.popupData.path_audience = null;
    }

    this.popupData = {
      ...this.popupData,
      name: pathName || '',
      path_id: pathId || null,
      popupType: popupType,
      offer_intent: offerIntent,
      path_steps: pathSteps && pathSteps.length ? pathSteps : [],
    };

    this.openPopup();
  }

  openPopup() {
    this.ngxSmartModalService.getModal('pathPopup').open();
  }

  openPathPreviewPopup(pathId: number) {
    this.popupData = {
      ...this.popupData,
      id: pathId,
    };

    this.ngxSmartModalService.getModal('pathPreviewPopup').open();
  }
  closePathPopup() {
    this.ngxSmartModalService.getModal('pathPopup').close();
  }

  savePath(data: { isNew: boolean; id: number }) {
    this.ngxSmartModalService.getModal('pathPopup').close();

    if (data.isNew) {
      this.router.navigate(['new-paths', 'edit', data.id]);
    } else {
      this.queryData();
    }
  }

  filterTableData() {
    const filteredData = [...this.originalTableData];
    switch (this.selectedFilter) {
      case 'name':
        filteredData.sort((a, b) => a.pathName.localeCompare(b.pathName));
        break;
      case 'type':
        filteredData.sort((a, b) => a.type.localeCompare(b.type));
        break;
      case 'status':
        filteredData.sort((a, b) => a.status.localeCompare(b.status));
        break;
      case 'audience':
        filteredData.sort((a, b) => a.audience.localeCompare(b.audience));
        break;
      case 'lastModified':
        this.selectedDate = 'lastModified';
        filteredData.sort((a, b) => {
          const dateA = moment(a.lastModified.date + ' ' + a.lastModified.time, 'DD MMM, YYYY HH:mm');
          const dateB = moment(b.lastModified.date + ' ' + b.lastModified.time, 'DD MMM, YYYY HH:mm');
          return dateB.diff(dateA);
        });
        break;
      default:
        break;
    }
    this.tableData = filteredData;
  }

  searchData() {
    if (!this.searchQuery) {
      this.tableData = [...this.originalTableData];
      this.groupedTableData = this.groupDataByType(this.originalTableData);
    } else {
      const searchTerm = this.searchQuery.toLowerCase();
      const filteredData = this.originalTableData.filter((item) => item.pathName.toLowerCase().includes(searchTerm));

      this.tableData = filteredData;
      this.groupedTableData = this.groupDataByType(filteredData);
    }
  }

  closeOtherMenus(currentItem: any): void {
    this.tableData.forEach((item) => {
      if (item.id !== currentItem.id) {
        item.showThreeDotMenu = false;
      }
    });
  }

  toggleThreeDotMenu(item: any, group: GroupedTableData, event: Event): void {
    event.stopPropagation();

    this.groupedTableData.forEach((g) => {
      g.items.forEach((i) => {
        if (i.id !== item.id) {
          i.showThreeDotMenu = false;
        }
      });
    });

    const groupItem = group.items.find((i) => i.id === item.id);
    if (groupItem) {
      groupItem.showThreeDotMenu = !groupItem.showThreeDotMenu;
    }
  }

  closeThreeDotMenu(item: any, group: GroupedTableData): void {
    const groupItem = group.items.find((i) => i.id === item.id);
    if (groupItem) {
      groupItem.showThreeDotMenu = false;
    }
  }

  toggleShowAllConditions(item: any): void {
    item.showAllConditions = !item.showAllConditions;
  }

  onFilterChanged(event: { filterName: string; selectedValues: string[] }): void {
    const { filterName, selectedValues } = event;
    let filteredData = [...this.originalTableData];

    if (selectedValues.length > 0 && !selectedValues.includes(null)) {
      switch (filterName) {
        case 'Status':
          filteredData = this.originalTableData.filter((item) => selectedValues.includes(item.status));
          break;

        case 'Type':
          filteredData = this.originalTableData.filter((item) => selectedValues.includes(item.type));
          break;
      }
    }

    this.tableData = filteredData;
    this.groupedTableData = this.groupDataByType(filteredData);
  }

  private groupDataByType(data: any[]): GroupedTableData[] {
    // Keep track of expanded state before regrouping
    const expandedStates = this.groupedTableData.reduce((acc, group) => {
      acc[group.type] = group.isExpanded;
      return acc;
    }, {} as Record<string, boolean>);

    const groupedByType = data.reduce((acc, item) => {
      const type = item.type;
      const label = item.typeLabel;
      if (!acc[type]) {
        acc[type] = {
          type,
          label,
          isExpanded: expandedStates[type] || false, // Restore expanded state or default to false
          items: [],
          lastModified: '',
        };
      }
      acc[type].items.push(item);
      return acc;
    }, {} as Record<string, GroupedTableData>);

    Object.values(groupedByType).forEach((group: GroupedTableData) => {
      const latestDate = group.items.reduce((latest: Date, item: any) => {
        const itemDate = new Date(`${item.lastModified.date} ${item.lastModified.time}`);
        return itemDate > latest ? itemDate : latest;
      }, new Date(0));

      group.lastModified = moment(latestDate).format('MM/DD/YYYY - hh:mm A');
    });

    return Object.values(groupedByType);
  }

  toggleAccordion(group: GroupedTableData) {
    if (group.isExpanded) {
      this.closeAllPopups(group);
      group.isExpanded = false;
      this.activeAccordion = null;
    } else {
      // Close all other accordions
      this.groupedTableData.forEach((g) => {
        if (g.type !== group.type) {
          this.closeAllPopups(g);
          g.isExpanded = false;
        }
      });

      group.isExpanded = true;
      this.activeAccordion = group.type;

      // Scroll to the expanded group
      scrollToAccordionGroup(group.type, this.groupedTableData, this.accordionGroups.toArray());
    }
  }

  closeAllPopups(group: GroupedTableData): void {
    group.items.forEach((item) => {
      item.showThreeDotMenu = false;
      item.pathPopup = false;
    });
  }

  isCancelType(type: OfferIntents) {
    return (
      [
        OfferIntents.CancelOrder,
        OfferIntents.CancelSubscription,
        OfferIntents.CancelSubscriptionHold,
        OfferIntents.CancelRebillHold,
        OfferIntents.CancelTrial,
        OfferIntents.CancelTrialHold,
      ].indexOf(type) > -1
    );
  }

  isReturnType(type: OfferIntents) {
    return [OfferIntents.ReturnOrder, OfferIntents.ReturnTrial, OfferIntents.ReturnSubscription].indexOf(type) > -1;
  }

  onPreview(previewUrl: string) {
    this.ngxSmartModalService.getModal('pathPreviewPopup').close();
    window.open(previewUrl, '_blank');
  }

  popupIconMap: Partial<Record<PathPopup, string>> = {
    [PathPopup.Create]: '/assets/stepAssets/create-icon.svg',
    [PathPopup.Delete]: '/assets/stepAssets/delete-icon.svg',
    [PathPopup.Duplicate]: '/assets/stepAssets/duplicate.svg',
    [PathPopup.Update]: '/assets/stepAssets/rename.svg',
  };
}
